import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AiAgentEvaluationCriteriaStatusName } from '../../core/constants/ai-agent-evaluation-criteria-status-name';
import { getLanguageNameByCode } from '../../core/functions/string-extention';
import { IAIAgentConversationHistory } from '../../core/interfaces/ai-agent/ai-agent-conversation-history';
import { IAIAgentConversationItem, IAIAgentConversationItemBase } from '../../core/interfaces/ai-agent/ai-agent-conversation-item.interface';
import { AiAgentPriceTypeLabels, AiAgentPriceTypes } from '../../core/interfaces/voice/voice-call-price-details';
import { IVoiceCallRecording } from '../../core/interfaces/voice/voice-call-recording.interface';
import { AIAgentService } from '../../core/services/ai-agent/ai-agent.service';

@Component({
  selector: 'app-ai-agent-conversation',
  templateUrl: './ai-agent-conversation.component.html',
  styleUrl: './ai-agent-conversation.component.scss',
  providers: [AIAgentService]
})
export class AiAgentConversationComponent implements OnInit {

  public loading: boolean = false;
  public height: Number = 0;

  //public id: any = null;
  public history: IAIAgentConversationHistory = null;
  public timeZoneId = "";
  public languageName: string;

  public criteriaStatus = AiAgentEvaluationCriteriaStatusName;
  public expandedTools: { [id: string]: boolean } = {};

  public systemTools = [
    "EndConversation",
    "DetectLanguage",
    "TransferToHuman",
    "TransferToAIAgent",
    "GetDaysFromToday"
  ]

  public tokenUsageSections: { name: string, items: { label: string, value: number, unit: string }[] }[] = [];

  private readonly usageSections = [
    {
      name: 'AUDIO',
      items: [
        { code: AiAgentPriceTypes.RealtimeAudioInput, label: 'Input' },
        { code: AiAgentPriceTypes.RealtimeAudioInputCached, label: 'Input cached' },
        { code: AiAgentPriceTypes.RealtimeAudioOutput, label: 'Output' }
      ]
    },
    {
      name: 'TEXT',
      items: [
        { code: AiAgentPriceTypes.RealtimeTextInput, label: 'Input' },
        { code: AiAgentPriceTypes.RealtimeTextInputCached, label: 'Input cached' },
        { code: AiAgentPriceTypes.RealtimeTextOutput, label: 'Output' }
      ]
    },
    {
      name: 'DELEGATION',
      items: [
        { code: AiAgentPriceTypes.AskGenerativeAIInput, label: 'Input' },
        { code: AiAgentPriceTypes.AskGenerativeAIInputCached, label: 'Input cached' },
        { code: AiAgentPriceTypes.AskGenerativeAIOutput, label: 'Output' },
        { code: AiAgentPriceTypes.AskGenerativeAIOutputCached, label: 'Output cached' }
      ]
    },
    {
      name: 'MINUTES',
      items: [
        { code: AiAgentPriceTypes.RealtimeMinute, label: 'Realtime' }
      ]
    }
  ];

  @Output() onCloseConversationDetails = new EventEmitter<any>();
  @Input() conversationId: any = null;
  @Input() voiceCallId: any = null;
  @Input() customerCode: any = '';
  @Input() recordings: IVoiceCallRecording[] = [];

  constructor(private aiAgentService: AIAgentService,
    private activatedRoute: ActivatedRoute,
    private router: Router) {
    //this.id = this.activatedRoute.snapshot.paramMap.get('entityId');
  }

  ngOnInit() {
    const observer = new ResizeObserver(entries => {
      //important! adjust the value to be substracted for a good fit of the conversation on the page 
      this.height = entries[0].contentRect.height - -450;
    });
    observer.observe(document.querySelector('body'));

    this.getConversation();
    this.timeZoneId = localStorage.getItem("timeZoneId");
  }

  getConversation() {
    this.loading = true;

    if (this.voiceCallId) {
      this.aiAgentService.getCompleteConversation(this.voiceCallId, this.customerCode).subscribe(response => {
        this.handleResponse(response);
      });
    }

    if (this.conversationId) {
      this.aiAgentService.getConversation(this.conversationId).subscribe(response => {
        this.handleResponse(response);
      });
    }
  }

  // The messages of the same participant (and agent) received at less than 1 second one after another are shown in the same bubble
  mergeConsecutiveMessages(conversation: IAIAgentConversationItem[]): IAIAgentConversationItem[] {
    const maxIntervalMs = 1000;
    const result: IAIAgentConversationItem[] = [];

    let lastMessage: IAIAgentConversationItem = null;
    let lastMessageDate: number = null;

    for (const msg of conversation ?? []) {
      if (msg?.type != 'Message' || msg.item == null) {
        //another type of item breaks the group
        result.push(msg);
        lastMessage = null;
        lastMessageDate = null;
        continue;
      }

      const date = msg.item.createdOnUtc ? new Date(msg.item.createdOnUtc).getTime() : null;

      const canMerge = lastMessage != null &&
        lastMessage.item.participantType == msg.item.participantType &&
        lastMessage.item.agentName == msg.item.agentName &&
        date != null && lastMessageDate != null &&
        Math.abs(date - lastMessageDate) < maxIntervalMs;

      if (canMerge) {
        lastMessage.item.text = [lastMessage.item.text, msg.item.text].filter(x => !!x).join(' ');
        lastMessageDate = date;
        continue;
      }

      //copy, the merged text must not change the response item
      lastMessage = { ...msg, item: { ...msg.item } };
      lastMessageDate = date;
      result.push(lastMessage);
    }

    return result;
  }

  handleResponse(response: IAIAgentConversationHistory) {
    this.loading = false;

    this.history = response;
    this.history.conversation = this.mergeConsecutiveMessages(this.history.conversation);
    this.languageName = this.getLanguageName(this.history.languageCode);

    if (this.history.tokenUsage != null) {
      this.history.tokenUsageList = [];
      Object.keys(this.history.tokenUsage).forEach(x => {

        this.history.tokenUsageList.push({
          serviceName: AiAgentPriceTypeLabels[x],
          //the RealtimeMinute usage is saved in seconds and it is charged per started minute
          tokens: x == AiAgentPriceTypes.RealtimeMinute ? Math.ceil(this.history.tokenUsage[x] / 60) : this.history.tokenUsage[x],
          isMinutes: x == AiAgentPriceTypes.RealtimeMinute
        });
      });

      this.tokenUsageSections = this.usageSections
        .map(section => ({
          name: section.name,
          items: section.items
            .filter(item => this.history.tokenUsage[item.code] != null)
            .map(item => ({
              label: item.label,
              //the RealtimeMinute usage is saved in seconds and it is charged per started minute
              value: item.code == AiAgentPriceTypes.RealtimeMinute ? Math.ceil(this.history.tokenUsage[item.code] / 60) : this.history.tokenUsage[item.code],
              unit: item.code == AiAgentPriceTypes.RealtimeMinute ? 'minutes' : 'tokens'
            }))
        }))
        .filter(section => section.items.length > 0);
    };

    if (this.history.recordings != null && this.history.recordings.length > 0 && this.recordings!=null && this.recordings.length>0) {
      this.history.recordings.forEach(item => {
        var record = this.recordings.find(x => x.url == item.url);

        if (record != null) {
          item.transcriptionUrl = record.transcriptionUrl;
        }
      });
    }
  }

  close() {
    //this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
    this.onCloseConversationDetails.emit();
  }

  //show the participant label only under the last message of a group; thinking / function call items do not break the group
  showParticipant(index: number): boolean {
    const conversation = this.history.conversation;
    const participantType = conversation[index].item?.participantType;

    for (let i = index + 1; i < conversation.length; i++) {
      if (conversation[i].type == 'Message' && conversation[i].item != null) {
        return conversation[i].item.participantType != participantType;
      }
    }

    return true;
  }

  getLanguageName(language: string): string {
    return getLanguageNameByCode(language);
  }

  toggleTool(index: number): void {
    this.expandedTools[index] = !this.expandedTools[index];
  }

  isToolSuccess(tool: IAIAgentConversationItemBase): boolean {
    if (tool.httpStatusCode==undefined || tool.httpStatusCode==null) {
      return !!tool.httpResponseBody;
    }
   
    return (tool.httpStatusCode == 0 && (tool.errorMessage == null || tool.errorMessage == '' )) || (tool.httpStatusCode >= 200 && tool.httpStatusCode < 300);
  }

  isSystemTool(tool: IAIAgentConversationItemBase): boolean {
    return this.systemTools.includes(tool.functionName);
  }

  isGetDaysFromToday(tool: IAIAgentConversationItemBase): boolean {
    return tool.functionName === "GetDaysFromToday";
  }

  // afișează buton de toggle dacă NU e tool de sistem, sau dacă e GetDaysFromToday
  showToggleButton(tool: IAIAgentConversationItemBase): boolean {
    return (!this.isSystemTool(tool) || this.isGetDaysFromToday(tool)) && this.hasToolDetails(tool);
  }

  // the tool has something to show in the details section
  hasToolDetails(tool: IAIAgentConversationItemBase): boolean {
    return !!tool.url || !!tool.body || !!tool.arguments || !!tool.httpResponseBody || !!tool.errorMessage;
  }

  // afișează doar URL-ul (fără toggle) dacă e tool de sistem (altul decât GetDaysFromToday) și are URL
  showUrlOnly(tool: IAIAgentConversationItemBase): boolean {
    return this.isSystemTool(tool) && !this.isGetDaysFromToday(tool) && !!tool.url;
  }

  prettyJson(value: string): string {
    if (!value) { return value; }
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  hasEntries(obj: { [key: string]: string } | null | undefined): boolean {
    return !!obj && Object.keys(obj).length > 0;
  }

}
